// API Service for Home Needs Refill
// Handles communication with the microservices API

import { apiConfig } from '../config/api';

const API_BASE_URL = apiConfig.baseUrl;
// Route orders via API Gateway rather than the order service directly
// This avoids CORS and centralizes routing/headers
// Use apiConfig.orderServiceUrl which is properly overridden for staging
const getOrderServiceBase = () => {
  const orderServiceUrl = apiConfig.orderServiceUrl || 'http://localhost:3005/api/v1';
  // Handle both relative URLs (staging) and absolute URLs (local)
  if (orderServiceUrl.startsWith('/')) {
    // Relative URL (staging) - nginx will proxy
    // On staging, orderServiceUrl is '/api', so we use '/api/orders' (no /v1)
    // This matches the pattern used by shops and products endpoints
    return `${orderServiceUrl}/orders`;
  } else {
    // Absolute URL (local) - orderServiceUrl is 'http://localhost:3005/api/v1'
    return `${orderServiceUrl}/orders`;
  }
};
const PAYMENT_SERVICE_URL = apiConfig.paymentServiceUrl;
// Coupon service is part of Order Service
const COUPON_SERVICE_BASE = `${apiConfig.orderServiceUrl}/coupons`;

export interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  category_id: string;
  topCategory: string;
  image: string;
  rating: number;
  reviews: number;
  inStock: boolean;
  discount: number;
  brand?: string;
  weight?: string;
  description?: string;
  sku?: string;
  is_active?: boolean;
  tags?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface Category {
  id: string;
  name: string;
  display_name: string;
  icon: string;
  top_category: string;
  description?: string;
  parent_id?: string;
  image_url?: string;
  is_active?: boolean;
  sort_order?: number;
  lvlcategory?: number;
  level?: number;
  created_at?: string;
  updated_at?: string;
}

export interface Coupon {
  id: string;
  code: string;
  title?: string;
  description?: string;
  discount_type: 'percentage' | 'fixed' | 'free_shipping';
  discount_value: number;
  minimum_order_amount?: number;
  maximum_discount?: number;
  applicable_to?: 'all' | 'products' | 'categories' | 'shops';
  product_ids?: string[];
  category_ids?: string[];
  shop_ids?: string[];
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  usage_limit?: number;
  usage_limit_per_user?: number;
  used_count?: number;
  eligible_user_types?: string[];
  excluded_user_ids?: string[];
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

export interface CouponValidationRequest {
  code: string;
  user_id: string;
  order_amount: number;
  items?: Array<{
    product_id: string;
    quantity: number;
    price?: number;
    category_id?: string;
  }>;
  shop_id?: string;
}

export interface CouponValidationResponse {
  valid: boolean;
  discount_amount: number;
  coupon?: Coupon;
  error?: string;
  message?: string;
}

export interface UserCoupon {
  id: string;
  user_id: string;
  coupon_id: string;
  coupon: Coupon;
  clipped_at: string;
  used_at?: string;
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    
    // Add auth token if available
    const token = localStorage.getItem('access_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // Async version for consistency with user code patterns
  private async getHeadersAsync(): Promise<Record<string, string>> {
    return this.getHeaders();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Get headers with auth token
    const authHeaders = this.getHeaders();
    
    const defaultOptions: RequestInit = {
      headers: {
        ...authHeaders,
        ...options.headers,
      },
      mode: 'cors',
      credentials: 'omit',
    };

    try {
      const response = await fetch(url, { ...defaultOptions, ...options });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: errorText, message: errorText };
        }
        const error = new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).response = errorData;
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('API request failed:', {
        url,
        endpoint,
        error: error?.message,
        status: error?.status,
        stack: error?.stack
      });
      
      // If it's not already an Error with status, it might be a network error
      if (!error.status && error.message) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          const networkError = new Error('Network error: Unable to connect to the server. Please check your connection.');
          (networkError as any).status = 0;
          (networkError as any).isNetworkError = true;
          throw networkError;
        }
      }
      
      throw error;
    }
  }

  // Products API - Updated for FastAPI product service
  async getProducts(params?: {
    category?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    brand?: string;
    inStock?: boolean;
    page?: number;
    limit?: number;
    main_category_id?: string;
    subcategory_id?: string;
    category_id?: string; // Add category_id parameter to match products.category_id
    main_category?: string;
    subcategory?: string;
  }): Promise<Product[]> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    // Use the new /list endpoint that returns real database data
    const endpoint = `/products/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log('🌐 Customer Web - API Request:', {
      endpoint: endpoint,
      params: params,
      queryString: queryParams.toString()
    });
    const products = await this.request<Product[]>(endpoint);
    console.log('🌐 Customer Web - Loaded products:', {
      count: products.length,
      searchParam: params?.search,
      firstProduct: products[0]?.name,
      allProductNames: products.map(p => p.name)
    });
    return products;
  }

  /**
   * Get products by multiple category IDs (Level 3 categories)
   * This is optimized for fetching products from multiple categories in a single API call
   * SQL equivalent: SELECT * FROM products WHERE category_id IN (categoryIds) AND discount > 0
   * 
   * @param categoryIds - Array of Level 3 category IDs
   * @param options - Additional filtering options
   * @returns Array of products matching the category IDs
   */
  async getProductsByCategoryIds(
    categoryIds: string[],
    options?: {
      discount?: boolean; // Filter by discount > 0
      minDiscount?: number; // Minimum discount percentage
      rating?: number; // Minimum rating
      limit?: number; // Maximum number of products to return
      inStock?: boolean; // Filter by inStock status
    }
  ): Promise<Product[]> {
    if (!categoryIds || categoryIds.length === 0) {
      console.warn('⚠️ getProductsByCategoryIds: No category IDs provided');
      return [];
    }

    try {
      // Use POST request to send array of category IDs in the body
      // This avoids URL length limits and is more efficient for multiple IDs
      // Endpoint: /api/v1/products/list-by-category-ids
      const endpoint = '/products/list-by-category-ids';
      
      const requestBody = {
        category_ids: categoryIds,
        ...(options?.discount !== undefined && { filter_discount: options.discount }),
        ...(options?.minDiscount !== undefined && { min_discount: options.minDiscount }),
        ...(options?.rating !== undefined && { min_rating: options.rating }),
        ...(options?.limit !== undefined && { limit: options.limit }),
        ...(options?.inStock !== undefined && { in_stock: options.inStock }),
      };

      const fullUrl = `${this.baseUrl}${endpoint}`;
      console.log(`🌐 Fetching products from: ${fullUrl}`);
      console.log(`🌐 Request body:`, { ...requestBody, category_ids: `[${categoryIds.length} IDs]` });
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Failed to fetch products by category IDs: ${response.status} - ${errorText}`);
        console.error(`❌ URL: ${fullUrl}`);
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const products = await response.json();
      console.log(`✅ Successfully loaded ${products.length} products for ${categoryIds.length} category IDs from ${endpoint}`);
      
      return products;
    } catch (error) {
      console.error('❌ Error in getProductsByCategoryIds:', error);
      // Fallback: Try individual calls if the new endpoint doesn't exist yet
      console.log('⚠️ Falling back to individual API calls per category ID');
      return this.getProductsByCategoryIdsFallback(categoryIds, options);
    }
  }

  /**
   * Fallback method: Makes individual API calls for each category ID
   * Used when the new endpoint is not yet available
   */
  private async getProductsByCategoryIdsFallback(
    categoryIds: string[],
    options?: {
      discount?: boolean;
      minDiscount?: number;
      rating?: number;
      limit?: number;
      inStock?: boolean;
    }
  ): Promise<Product[]> {
    const allProducts: Product[] = [];
    
    // Make parallel requests for each category ID
    const productPromises = categoryIds.map(async (categoryId) => {
      try {
        const products = await this.getProducts({
          category_id: categoryId,
          limit: options?.limit || 50,
        });
        return products;
      } catch (error) {
        console.error(`❌ Failed to fetch products for category ID ${categoryId}:`, error);
        return [];
      }
    });

    const productArrays = await Promise.all(productPromises);
    
    // Flatten and deduplicate products
    const productMap = new Map<string, Product>();
    productArrays.flat().forEach(product => {
      if (!productMap.has(product.id)) {
        productMap.set(product.id, product);
      }
    });

    allProducts.push(...Array.from(productMap.values()));

    // Apply filters
    let filteredProducts = allProducts;
    
    if (options?.discount) {
      filteredProducts = filteredProducts.filter(p => (p.discount || 0) > 0);
    }
    
    if (options?.minDiscount !== undefined) {
      filteredProducts = filteredProducts.filter(p => (p.discount || 0) >= options.minDiscount!);
    }
    
    if (options?.rating !== undefined) {
      filteredProducts = filteredProducts.filter(p => (p.rating || 0) >= options.rating!);
    }
    
    if (options?.inStock !== undefined) {
      filteredProducts = filteredProducts.filter(p => p.inStock === options.inStock);
    }

    // Apply limit
    if (options?.limit !== undefined) {
      filteredProducts = filteredProducts.slice(0, options.limit);
    }

    console.log(`✅ Fallback: Loaded ${filteredProducts.length} products from ${categoryIds.length} category IDs`);
    return filteredProducts;
  }

  async getProduct(id: string): Promise<Product> {
    return this.request<Product>(`/products/${id}`);
  }

  async createProduct(productData: Partial<Product>): Promise<Product> {
    return this.request<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  }

  async updateProduct(id: string, productData: Partial<Product>): Promise<Product> {
    return this.request<Product>(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  }

  async deleteProduct(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/products/${id}`, {
      method: 'DELETE',
    });
  }

  // Categories API - Updated for FastAPI structure
  async getCategories(mainCategoryId?: string): Promise<Category[]> {
    // Use the new /list endpoint that returns real database data
    const url = mainCategoryId 
      ? `/categories/list?main_category_id=${mainCategoryId}`
      : '/categories/list';
    return this.request<Category[]>(url);
  }

  // Get main categories only
  async getMainCategories(): Promise<Category[]> {
    return this.request<Category[]>('/categories/main-categories');
  }

  // Get categories in hierarchical structure
  async getCategoriesHierarchy(): Promise<any> {
    return this.request<any>('/categories/hierarchy');
  }

  // Get products by category
  async getProductsByCategory(categoryName: string): Promise<Product[]> {
    return this.request<Product[]>(`/products/by-category/${categoryName}`);
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/categories/${id}`);
  }

  async getCategoryHierarchy(): Promise<Category[]> {
    return this.request<Category[]>('/categories/hierarchy/tree');
  }

  async createCategory(categoryData: Partial<Category>): Promise<Category> {
    return this.request<Category>('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async updateCategory(id: string, categoryData: Partial<Category>): Promise<Category> {
    return this.request<Category>(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  async deleteCategory(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Orders API
  async createOrder(orderData: any): Promise<any> {
    try {
      // Validate payload structure
      if (!orderData.user_id) {
        throw new Error('Missing required field: user_id');
      }
      if (!orderData.shop_id) {
        throw new Error('Missing required field: shop_id');
      }
      if (!orderData.items || !Array.isArray(orderData.items) || orderData.items.length === 0) {
        throw new Error('Missing or empty items array');
      }
      if (!orderData.shipping_address_id) {
        throw new Error('Missing required field: shipping_address_id');
      }
      
      // Validate each item
      orderData.items.forEach((item: any, index: number) => {
        if (!item.product_id) {
          throw new Error(`Item ${index}: Missing product_id`);
        }
        if (!item.product_name) {
          throw new Error(`Item ${index}: Missing product_name`);
        }
        if (item.quantity === undefined || item.quantity <= 0) {
          throw new Error(`Item ${index}: Invalid quantity`);
        }
        if (item.unit_price === undefined || item.unit_price <= 0) {
          throw new Error(`Item ${index}: Invalid unit_price`);
        }
      });
      
      console.log('🛒 Customer-Web API: Creating order with validated data:', {
        user_id: orderData.user_id,
        shop_id: orderData.shop_id,
        items_count: orderData.items.length,
        shipping_address_id: orderData.shipping_address_id,
        payment_method: orderData.payment_method,
        total_amount: orderData.total_amount,
        subtotal: orderData.subtotal,
        shipping_amount: orderData.shipping_amount,
        tax_amount: orderData.tax_amount
      });
      const orderBase = getOrderServiceBase();
      console.log('🛒 Customer-Web API: Using endpoint:', `${orderBase}/create`);
      console.log('🛒 Customer-Web API: Full payload:', JSON.stringify(orderData, null, 2));
      
      const response = await fetch(`${orderBase}/create`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(orderData),
      });
      
      console.log('🛒 Customer-Web API: Response status:', response.status);
      console.log('🛒 Customer-Web API: Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🛒 Customer-Web API: Order creation failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          payload: orderData
        });
        throw new Error(`Order creation failed (${response.status}): ${errorText}`);
      }
      
      const responseData = await response.json();
      console.log('🛒 Customer-Web API: Order creation response:', responseData);
      console.log('🛒 Customer-Web API: Response structure:', {
        has_id: !!responseData.id,
        has_order_id: !!responseData.order_id,
        has_orderId: !!responseData.orderId,
        has_order_number: !!responseData.order_number,
        has_items: !!responseData.items,
        has_total_amount: !!responseData.total_amount,
        keys: Object.keys(responseData)
      });
      
      return responseData;
    } catch (error: any) {
      console.error('🛒 Customer-Web API: Error creating order:', error);
      console.error('🛒 Customer-Web API: Error details:', {
        message: error?.message,
        stack: error?.stack,
        payload: orderData
      });
      throw error;
    }
  }

  async getOrder(orderId: string): Promise<any> {
    try {
      const orderBase = getOrderServiceBase();
      console.log('🛒 Customer-Web API: Fetching order with ID:', orderId);
      console.log('🛒 Customer-Web API: Using endpoint:', `${orderBase}/${orderId}`);
      
      const response = await fetch(`${orderBase}/${orderId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      
      console.log('🛒 Customer-Web API: Get order response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🛒 Customer-Web API: Order retrieval failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        
        if (response.status === 404) {
          throw new Error(`Order not found. The order with ID ${orderId} does not exist in the database.`);
        }
        
        throw new Error(`Order retrieval failed: ${errorText}`);
      }
      
      const orderData = await response.json();
      console.log('🛒 Customer-Web API: Order data received:', orderData);
      return orderData;
    } catch (error) {
      console.error('🛒 Customer-Web API: Error getting order:', error);
      throw error;
    }
  }

  async getUserOrders(userId?: string): Promise<any[]> {
    try {
      const orderBase = getOrderServiceBase();
      const endpoint = userId ? `${orderBase}/user/${userId}` : `${orderBase}/all`;
      console.log('🛒 Customer-Web API: Getting user orders from:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      
      console.log('🛒 Customer-Web API: Orders response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🛒 Customer-Web API: User orders retrieval failed:', errorText);
        throw new Error(`User orders retrieval failed: ${errorText}`);
      }
      
      const orders = await response.json();
      console.log('🛒 Customer-Web API: Retrieved orders:', orders);
      
      return orders;
    } catch (error) {
      console.error('🛒 Customer-Web API: Error getting user orders:', error);
      throw error;
    }
  }

  // Payments API
  async createPayment(paymentData: any): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/mock-create`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(paymentData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Payment creation failed: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  }

  async getPayment(paymentId: string): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/${paymentId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Payment retrieval failed: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting payment:', error);
      throw error;
    }
  }

  // User Addresses API
  async getUserAddresses(): Promise<any[]> {
    try {
      console.log('🛒 Customer-Web API: Fetching user addresses');
      console.log('🛒 Customer-Web API: Using endpoint:', `${this.baseUrl}/users/addresses`);
      
      const addresses = await this.request<any[]>('/users/addresses');
      console.log('🛒 Customer-Web API: User addresses received:', addresses);
      console.log('🛒 Customer-Web API: Addresses count:', addresses?.length || 0);
      
      return addresses || [];
    } catch (error: any) {
      console.error('❌ Customer-Web API: Error fetching user addresses:', error);
      console.error('❌ Customer-Web API: Error details:', {
        message: error?.message,
        status: error?.status,
        response: error?.response
      });
      throw error;
    }
  }

  async createAddress(addressData: any): Promise<any> {
    return this.request<any>('/users/addresses', {
      method: 'POST',
      body: JSON.stringify(addressData),
    });
  }

  async updateAddress(addressId: string, addressData: any): Promise<any> {
    return this.request<any>(`/users/addresses/${addressId}`, {
      method: 'PUT',
      body: JSON.stringify(addressData),
    });
  }

  async deleteAddress(addressId: string): Promise<void> {
    return this.request<void>(`/users/addresses/${addressId}`, {
      method: 'DELETE',
    });
  }

  async setDefaultAddress(addressId: string): Promise<any> {
    return this.request<any>(`/users/addresses/${addressId}/set-default`, {
      method: 'PATCH',
    });
  }

  // Payment Methods API
  async getPaymentMethods(token?: string): Promise<any[]> {
    try {
      const headers = this.getHeaders();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/methods`, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Payment methods retrieval failed: ${errorText}`);
      }
      
      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  }

  async createPaymentMethod(token: string, paymentMethodData: any): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/methods`, {
        method: 'POST',
        headers: {
          ...this.getHeaders(),
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(paymentMethodData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Payment method creation failed: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating payment method:', error);
      throw error;
    }
  }

  async updatePaymentMethod(token: string, methodId: string, paymentMethodData: any): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/methods/${methodId}`, {
        method: 'PUT',
        headers: {
          ...this.getHeaders(),
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(paymentMethodData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Payment method update failed: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating payment method:', error);
      throw error;
    }
  }

  async deletePaymentMethod(token: string, methodId: string): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/methods/${methodId}`, {
        method: 'DELETE',
        headers: {
          ...this.getHeaders(),
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Payment method deletion failed: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting payment method:', error);
      throw error;
    }
  }

  async processPayment(paymentData: any): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/process`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(paymentData),
      });
      return await response.json();
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }

  async verifyPayment(transactionId: string): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/verify`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ transaction_id: transactionId }),
      });
      return await response.json();
    } catch (error) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  }

  async getPaymentStatus(transactionId: string): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/status/${transactionId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      console.error('Error getting payment status:', error);
      throw error;
    }
  }

  async refundPayment(transactionId: string, amount: number, reason: string = 'Customer request'): Promise<any> {
    try {
      const response = await fetch(`${PAYMENT_SERVICE_URL}/payments/refund`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          transaction_id: transactionId,
          amount: amount,
          reason: reason,
        }),
      });
      return await response.json();
    } catch (error) {
      console.error('Error processing refund:', error);
      throw error;
    }
  }

  // Shop API - Get all shops for customer browsing
  // Use shopServiceUrl (port 3003) instead of baseUrl (port 3004)
  async getShops(): Promise<any[]> {
    try {
      // Use API gateway (port 3000) - same as shop dashboard endpoint
      // This ensures we get shop data with logos
      const shopServiceUrl = (apiConfig as any).shopServiceUrl || 'http://localhost:3000/api';
      console.log('🏪 Fetching shops from API gateway:', shopServiceUrl);
      console.log('📍 Using same endpoint as shop dashboard to ensure logos are included');
      
      // Handle both relative URLs (staging) and absolute URLs (local)
      let url: string;
      if (shopServiceUrl.startsWith('/')) {
        // Relative URL (staging) - use string concatenation with URLSearchParams
        const params = new URLSearchParams();
        params.append('limit', '1000');
        params.append('page', '1');
        params.append('all', 'true');
        url = `${shopServiceUrl}/shops?${params.toString()}`;
      } else {
        // Absolute URL (local) - use URL constructor
        const urlObj = new URL(`${shopServiceUrl}/shops`);
        urlObj.searchParams.append('limit', '1000');
        urlObj.searchParams.append('page', '1');
        urlObj.searchParams.append('all', 'true');
        url = urlObj.toString();
      }
      
      console.log('📡 HTTP Request:', {
        method: 'GET',
        url: url,
        headers: this.getHeaders()
      });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });
      
      console.log('📥 HTTP Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch shops: ${response.status}`);
      }
      
      const data = await response.json();
      const shops = data.shops || data;
      
      console.log('✅ Shops received from API:', {
        totalShops: Array.isArray(shops) ? shops.length : 0,
        shopNames: Array.isArray(shops) ? shops.map((s: any) => s.name) : [],
        rawData: data
      });
      
      // Log the equivalent SQL query that would be executed on the backend
      console.log('💾 Equivalent SQL Query (executed on backend):');
      console.log(`
        SELECT 
          s.*,
          COUNT(DISTINCT p.id) as product_count
        FROM shops s
        LEFT JOIN products p ON p.shop_id = s.id
        WHERE s.is_active = true
        GROUP BY s.id
        ORDER BY s.created_at DESC
        LIMIT 1000;
      `);
      
      return shops;
    } catch (error) {
      console.error('❌ Error fetching shops:', error);
      throw error;
    }
  }

  // Get products from a specific shop
  async getShopProducts(shopId: string, filters?: {
    category?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
  }): Promise<Product[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.minPrice) params.append('minPrice', filters.minPrice.toString());
      if (filters?.maxPrice) params.append('maxPrice', filters.maxPrice.toString());

      const queryString = params.toString();
      const url = `${this.baseUrl}/shops/${shopId}/products${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch shop products: ${response.status}`);
      }
      
      const data = await response.json();
      return data.products || data;
    } catch (error) {
      console.error('Error fetching shop products:', error);
      throw error;
    }
  }

  // Search products across all shops
  async searchAllShops(query: string, filters?: {
    shopType?: string;
    category?: string;
    minPrice?: number;
    maxPrice?: number;
  }): Promise<{ shop: any; products: Product[] }[]> {
    try {
      const params = new URLSearchParams();
      params.append('q', query);
      if (filters?.shopType) params.append('shopType', filters.shopType);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.minPrice) params.append('minPrice', filters.minPrice.toString());
      if (filters?.maxPrice) params.append('maxPrice', filters.maxPrice.toString());

      const response = await fetch(`${this.baseUrl}/search?${params.toString()}`, {
        method: 'GET',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching products:', error);
      // Return mock data as fallback
      return this.getMockSearchResults(query, filters);
    }
  }

  // Mock search results for fallback
  private getMockSearchResults(query: string, filters?: any): { shop: any; products: Product[] }[] {
    const mockShops = [
      {
        id: 'shop-1',
        name: 'Airport Terminal Shop',
        type: 'grocery',
        address: 'Terminal 1, Gate A12, Airport Plaza'
      },
      {
        id: 'shop-2',
        name: 'City Pharmacy',
        type: 'medical',
        address: '123 Health Street, Medical District'
      },
      {
        id: 'shop-3',
        name: 'Spice Palace Indian Bakery',
        type: 'bakery',
        address: '456 Curry Lane, Little India'
      }
    ];

    const mockProducts: Product[] = [
      {
        id: 'prod-1',
        name: 'Fresh Apples',
        price: 2.99,
        category: 'Fruits',
        category_id: 'cat-1',
        topCategory: 'groceries',
        image: 'ðŸŽ',
        rating: 4.5,
        reviews: 120,
        inStock: true,
        discount: 0,
        brand: 'Fresh Farm',
        description: 'Crisp and sweet apples'
      },
      {
        id: 'prod-2',
        name: 'Pain Relief Tablets',
        price: 8.99,
        category: 'Medications',
        category_id: 'cat-2',
        topCategory: 'medical',
        image: 'ðŸ’Š',
        rating: 4.2,
        reviews: 85,
        inStock: true,
        discount: 10,
        brand: 'HealthPlus',
        description: 'Fast-acting pain relief'
      },
      {
        id: 'prod-3',
        name: 'Naan Bread',
        price: 3.49,
        category: 'Indian Breads',
        category_id: 'cat-3',
        topCategory: 'bakery',
        image: 'ðŸ¥–',
        rating: 4.8,
        reviews: 200,
        inStock: true,
        discount: 0,
        brand: 'Spice Palace',
        description: 'Fresh baked naan bread'
      }
    ];

    // Filter products based on query and filters
    let filteredProducts = mockProducts.filter(product => 
      product.name.toLowerCase().includes(query.toLowerCase()) ||
      product.description?.toLowerCase().includes(query.toLowerCase()) ||
      product.brand?.toLowerCase().includes(query.toLowerCase())
    );

    if (filters?.shopType && filters.shopType !== 'all') {
      filteredProducts = filteredProducts.filter(product => 
        product.topCategory === filters.shopType
      );
    }

    if (filters?.category && filters.category !== 'all') {
      filteredProducts = filteredProducts.filter(product => 
        product.category.toLowerCase().includes(filters.category.toLowerCase())
      );
    }

    if (filters?.minPrice) {
      filteredProducts = filteredProducts.filter(product => product.price >= filters.minPrice);
    }

    if (filters?.maxPrice) {
      filteredProducts = filteredProducts.filter(product => product.price <= filters.maxPrice);
    }

    // Group products by shop
    const results: { shop: any; products: Product[] }[] = [];
    
    mockShops.forEach(shop => {
      const shopProducts = filteredProducts.filter(product => {
        if (shop.type === 'grocery') return product.topCategory === 'groceries';
        if (shop.type === 'medical') return product.topCategory === 'medical';
        if (shop.type === 'bakery') return product.topCategory === 'bakery';
        return false;
      });

      if (shopProducts.length > 0) {
        results.push({
          shop,
          products: shopProducts
        });
      }
    });

    return results;
  }

  // Get categories for search filters (overload)
  async getCategoriesForSearch(level?: number, parentId?: string, parentCategoryName?: string): Promise<Category[]> {
    try {
      const params = new URLSearchParams();
      if (level !== undefined) {
        params.append('lvlcategory', level.toString());
      }
      if (parentId) {
        params.append('parent_id', parentId);
      }
      if (parentCategoryName) {
        params.append('parent_category', parentCategoryName);
      }
      
      const queryString = params.toString();
      const url = `${this.baseUrl}/categories${queryString ? `?${queryString}` : ''}`;
      
      console.log('🌐 Fetching categories from API:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.status}`);
      }
      
      const data = await response.json();
      const categories = data.categories || data;
      
      // Filter by level if not already filtered by API
      let filtered = categories;
      if (level !== undefined) {
        filtered = categories.filter((cat: Category) => 
          (cat.lvlcategory === level || cat.level === level)
        );
      }
      
      // Additional client-side filtering by parent_id if provided and not filtered by API
      if (parentId && filtered.length > 0) {
        const parentFiltered = filtered.filter((cat: Category) => cat.parent_id === parentId);
        if (parentFiltered.length > 0) {
          console.log(`🔍 Client-side filtered ${filtered.length} categories to ${parentFiltered.length} by parent_id: ${parentId}`);
          return parentFiltered;
        }
      }
      
      // Additional client-side filtering by parent category name if provided
      if (parentCategoryName && filtered.length > 0) {
        const parentNameLower = parentCategoryName.toLowerCase();
        const parentNameFiltered = filtered.filter((cat: Category) => {
          const catTopCategory = (cat.top_category || '').toLowerCase();
          const catParentName = (cat.parent_id || '').toLowerCase();
          return catTopCategory.includes(parentNameLower) || 
                 parentNameLower.includes(catTopCategory) ||
                 catParentName.includes(parentNameLower);
        });
        if (parentNameFiltered.length > 0) {
          console.log(`🔍 Client-side filtered ${filtered.length} categories to ${parentNameFiltered.length} by parent category: ${parentCategoryName}`);
          return parentNameFiltered;
        }
      }
      
      return filtered;
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Return mock categories as fallback
      return [
        { id: 'cat-1', name: 'Fruits', display_name: 'Fresh Fruits', icon: '🍎', top_category: 'groceries', lvlcategory: 2 },
        { id: 'cat-2', name: 'Medications', display_name: 'Medications', icon: '💊', top_category: 'medical', lvlcategory: 2 },
        { id: 'cat-3', name: 'Indian Breads', display_name: 'Indian Breads', icon: '🥖', top_category: 'bakery', lvlcategory: 2 },
        { id: 'cat-4', name: 'Vegetables', display_name: 'Fresh Vegetables', icon: '🥬', top_category: 'groceries', lvlcategory: 2 },
        { id: 'cat-5', name: 'Dairy', display_name: 'Dairy Products', icon: '🥛', top_category: 'groceries', lvlcategory: 2 }
      ];
    }
  }

  // Search categories by query (matching mobile app)
  async searchCategories(query: string, limit: number = 20): Promise<Category[]> {
    try {
      const params = new URLSearchParams();
      params.append('search', query);
      params.append('limit', limit.toString());
      
      const url = `${this.baseUrl}/categories/search?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to search categories: ${response.status}`);
      }
      
      const data = await response.json();
      return data.categories || data || [];
    } catch (error) {
      console.error('Error searching categories:', error);
      // Fallback: filter categories client-side
      try {
        const allCategories = await this.getCategoriesForSearch();
        const queryLower = query.toLowerCase();
        return allCategories
          .filter(cat => 
            cat.name.toLowerCase().includes(queryLower) ||
            cat.display_name.toLowerCase().includes(queryLower) ||
            cat.description?.toLowerCase().includes(queryLower)
          )
          .slice(0, limit);
      } catch (fallbackError) {
        console.error('Fallback category search failed:', fallbackError);
        return [];
      }
    }
  }

  // Search brands by query (matching mobile app)
  async searchBrands(query: string, limit: number = 20): Promise<Array<{ name: string; productCount?: number }>> {
    try {
      const params = new URLSearchParams();
      params.append('search', query);
      params.append('limit', limit.toString());
      
      const url = `${this.baseUrl}/brands/search?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to search brands: ${response.status}`);
      }
      
      const data = await response.json();
      return data.brands || data || [];
    } catch (error) {
      console.error('Error searching brands:', error);
      // Fallback: extract brands from products
      try {
        const products = await this.getProducts({ search: query, limit: 100 });
        const brandMap = new Map<string, number>();
        
        products.forEach(product => {
          if (product.brand) {
            const brand = product.brand.toLowerCase();
            if (brand.includes(query.toLowerCase())) {
              brandMap.set(product.brand, (brandMap.get(product.brand) || 0) + 1);
            }
          }
        });
        
        return Array.from(brandMap.entries())
          .map(([name, count]) => ({ name, productCount: count }))
          .slice(0, limit);
      } catch (fallbackError) {
        console.error('Fallback brand search failed:', fallbackError);
        return [];
      }
    }
  }


  // Health check - Use API Gateway
  async healthCheck(): Promise<{ service: string; status: string; version: string }> {
    const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      credentials: 'omit',
    });
    
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }
    
    return response.json();
  }

  // Test endpoint for product service
  async testProductService(): Promise<{ message: string; timestamp: string }> {
    return this.request<{ message: string; timestamp: string }>('/products/test');
  }

  // Coupons API - Part of Order Service
  async getAvailableCoupons(params?: {
    user_id?: string;
    shop_id?: string;
    min_order_amount?: number;
  }): Promise<Coupon[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.user_id) queryParams.append('user_id', params.user_id);
      if (params?.shop_id) queryParams.append('shop_id', params.shop_id);
      if (params?.min_order_amount) queryParams.append('min_order_amount', params.min_order_amount.toString());

      const url = `${COUPON_SERVICE_BASE}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🎫 API: Fetching coupons from:', url);
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Failed to fetch coupons: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch {
          // Not JSON, use text as is
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Handle multiple response formats
      let coupons: Coupon[] = [];
      if (Array.isArray(data)) {
        coupons = data;
      } else if (data && typeof data === 'object') {
        // Try common property names
        coupons = data.coupons || data.data || data.items || data.results || [];
      }
      
      if (process.env.NODE_ENV === 'development' && coupons.length > 0) {
        console.log(`✅ API: Loaded ${coupons.length} coupons`);
      }
      
      return coupons;
    } catch (error: any) {
      console.error('❌ API: Error fetching coupons:', error);
      console.error('❌ API: Error name:', error?.name);
      console.error('❌ API: Error message:', error?.message);
      console.error('❌ API: Error stack:', error?.stack);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('❌ API: Network error - Check if backend is running and CORS is configured');
      }
      throw error;
    }
  }

  async validateCoupon(request: CouponValidationRequest): Promise<CouponValidationResponse> {
    try {
      console.log('🎫 Validating coupon:', request.code);
      const url = `${COUPON_SERVICE_BASE}/validate`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }
        console.error('❌ Coupon validation failed:', errorData);
        return {
          valid: false,
          discount_amount: 0,
          error: errorData.error || errorData.message || `Validation failed: ${response.status}`,
        };
      }

      const data = await response.json();
      console.log('✅ Coupon validation response:', data);
      return data;
    } catch (error: any) {
      console.error('Error validating coupon:', error);
      return {
        valid: false,
        discount_amount: 0,
        error: error.message || 'Failed to validate coupon',
      };
    }
  }

  async getUserCoupons(userId: string): Promise<UserCoupon[]> {
    try {
      console.log('🎫 Fetching user coupons for:', userId);
      const url = `${COUPON_SERVICE_BASE}/user/${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Failed to fetch user coupons:', errorText);
        throw new Error(`Failed to fetch user coupons: ${response.status}`);
      }

      const data = await response.json();
      // Handle both array response and object with coupons property
      const userCoupons = Array.isArray(data) ? data : (data.coupons || data.user_coupons || []);
      console.log(`✅ Successfully loaded ${userCoupons.length} user coupons`);
      console.log('🎫 User coupons data structure:', JSON.stringify(userCoupons, null, 2));
      
      // Ensure each userCoupon has a coupon object, or try to reconstruct it
      const validUserCoupons = userCoupons.map((uc: any) => {
        // If coupon is missing but we have coupon_id, we might need to fetch it separately
        // For now, just filter out invalid entries
        if (!uc.coupon && uc.coupon_id) {
          console.warn('⚠️ UserCoupon missing coupon object:', uc);
        }
        return uc;
      }).filter((uc: any) => uc.coupon); // Filter out entries without coupon object
      
      console.log(`✅ Filtered to ${validUserCoupons.length} valid user coupons`);
      return validUserCoupons;
    } catch (error) {
      console.error('Error fetching user coupons:', error);
      throw error;
    }
  }

  async clipCoupon(couponId: string, userId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🎫 Clipping coupon:', couponId, 'for user:', userId);
      const url = `${COUPON_SERVICE_BASE}/${couponId}/clip`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ user_id: userId }),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }
        console.error('❌ Failed to clip coupon:', errorData);
        throw new Error(errorData.error || errorData.message || `Failed to clip coupon: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Coupon clipped successfully:', data);
      return { success: true, message: data.message };
    } catch (error: any) {
      console.error('Error clipping coupon:', error);
      throw error;
    }
  }

  async removeClippedCoupon(couponId: string, userId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🎫 Removing clipped coupon:', couponId, 'for user:', userId);
      const url = `${COUPON_SERVICE_BASE}/${couponId}/clip`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders(),
        body: JSON.stringify({ user_id: userId }),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText };
        }
        console.error('❌ Failed to remove clipped coupon:', errorData);
        throw new Error(errorData.error || errorData.message || `Failed to remove coupon: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Clipped coupon removed successfully:', data);
      return { success: true, message: data.message };
    } catch (error: any) {
      console.error('Error removing clipped coupon:', error);
      throw error;
    }
  }

  // Search shops that have a specific product
  async searchShopsByProduct(productName: string): Promise<Array<{ shop: any; product: Product; price: number; distance?: number }>> {
    try {
      const headers = await this.getHeadersAsync();
      // Search for shops that have this product
      // The endpoint is routed through API gateway at /api/search/shops-by-product
      // baseUrl is like http://localhost:3001/api/v1, so we need to go up one level
      const gatewayBase = this.baseUrl.replace('/api/v1', '');
      const response = await fetch(`${gatewayBase}/api/search/shops-by-product?product=${encodeURIComponent(productName)}`, {
        method: 'GET',
        headers,
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error('Failed to search shops by product');
      }
      
      const data = await response.json();
      return data.results || [];
    } catch (error) {
      console.error('Error searching shops by product:', error);
      // Fallback: Get all shops and check their products
      try {
        const shops = await this.getShops();
        const results: Array<{ shop: any; product: Product; price: number }> = [];
        
        for (const shop of shops) {
          try {
            const products = await this.getShopProducts(shop.id, { search: productName });
            if (products.length > 0) {
              const product = products[0]; // Get first matching product
              results.push({
                shop,
                product,
                price: product.price,
              });
            }
          } catch (err) {
            // Skip shops that fail
            continue;
          }
        }
        
        return results;
      } catch (fallbackError) {
        console.error('Fallback search failed:', fallbackError);
        throw error;
      }
    }
  }

  // Wishlist API
  async getWishlist(userId?: string): Promise<Product[]> {
    try {
      const userIdParam = userId || this.getUserIdFromToken();
      if (!userIdParam) {
        throw new Error('User ID is required for wishlist operations');
      }
      
      console.log('❤️ Fetching wishlist for user:', userIdParam);
      const wishlist = await this.request<Product[]>(`/users/${userIdParam}/wishlist`);
      console.log('✅ Wishlist loaded:', wishlist?.length || 0, 'items');
      return wishlist || [];
    } catch (error: any) {
      console.error('Error fetching wishlist:', error);
      // Fallback to localStorage if API fails
      try {
        const saved = localStorage.getItem('wishlist');
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    }
  }

  async addToWishlist(userId: string, productId: string, productData?: Partial<Product>): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('❤️ Adding product to wishlist:', productId, 'for user:', userId);
      const response = await this.request<{ success: boolean; message?: string }>(`/users/${userId}/wishlist`, {
        method: 'POST',
        body: JSON.stringify({
          product_id: productId,
          product: productData
        }),
      });
      
      // Sync to localStorage as backup
      try {
        const currentWishlist = await this.getWishlist(userId);
        localStorage.setItem('wishlist', JSON.stringify(currentWishlist));
      } catch {}
      
      console.log('✅ Product added to wishlist');
      return response;
    } catch (error: any) {
      console.error('Error adding to wishlist:', error);
      // Fallback: save to localStorage
      try {
        const saved = localStorage.getItem('wishlist') || '[]';
        const wishlist = JSON.parse(saved);
        const exists = wishlist.some((item: any) => String(item.id) === String(productId));
        if (!exists && productData) {
          wishlist.push({ id: productId, ...productData });
          localStorage.setItem('wishlist', JSON.stringify(wishlist));
        }
      } catch {}
      throw error;
    }
  }

  async removeFromWishlist(userId: string, productId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('❤️ Removing product from wishlist:', productId, 'for user:', userId);
      const response = await this.request<{ success: boolean; message?: string }>(`/users/${userId}/wishlist/${productId}`, {
        method: 'DELETE',
      });
      
      // Sync to localStorage as backup
      try {
        const currentWishlist = await this.getWishlist(userId);
        localStorage.setItem('wishlist', JSON.stringify(currentWishlist));
      } catch {}
      
      console.log('✅ Product removed from wishlist');
      return response;
    } catch (error: any) {
      console.error('Error removing from wishlist:', error);
      // Fallback: remove from localStorage
      try {
        const saved = localStorage.getItem('wishlist') || '[]';
        const wishlist = JSON.parse(saved);
        const updated = wishlist.filter((item: any) => String(item.id) !== String(productId));
        localStorage.setItem('wishlist', JSON.stringify(updated));
      } catch {}
      throw error;
    }
  }

  // Loyalty Points API
  async getLoyaltyPoints(userId?: string): Promise<{ points: number; transactions?: any[] }> {
    try {
      const userIdParam = userId || this.getUserIdFromToken();
      if (!userIdParam) {
        throw new Error('User ID is required for loyalty points operations');
      }
      
      console.log('⭐ Fetching loyalty points for user:', userIdParam);
      const data = await this.request<{ points: number; transactions?: any[] }>(`/users/${userIdParam}/loyalty-points`);
      console.log('✅ Loyalty points loaded:', data.points);
      return data;
    } catch (error: any) {
      console.error('Error fetching loyalty points:', error);
      // Fallback: get from user data or localStorage
      try {
        const saved = localStorage.getItem('loyalty_transactions');
        const transactions = saved ? JSON.parse(saved) : [];
        const points = transactions.reduce((sum: number, t: any) => {
          return sum + (t.type === 'earned' ? t.amount : -t.amount);
        }, 0);
        return { points: Math.max(0, points), transactions };
      } catch {
        return { points: 0, transactions: [] };
      }
    }
  }

  async updateLoyaltyPoints(userId: string, points: number, transaction: { type: 'earned' | 'redeemed'; amount: number; description: string }): Promise<{ success: boolean; new_balance: number }> {
    try {
      console.log('⭐ Updating loyalty points for user:', userId, 'points:', points);
      const response = await this.request<{ success: boolean; new_balance: number }>(`/users/${userId}/loyalty-points`, {
        method: 'PUT',
        body: JSON.stringify({
          points,
          transaction
        }),
      });
      
      // Sync transaction to localStorage as backup
      try {
        const saved = localStorage.getItem('loyalty_transactions') || '[]';
        const transactions = JSON.parse(saved);
        transactions.push({
          id: Date.now().toString(),
          ...transaction,
          date: new Date().toISOString()
        });
        localStorage.setItem('loyalty_transactions', JSON.stringify(transactions));
      } catch {}
      
      console.log('✅ Loyalty points updated, new balance:', response.new_balance);
      return response;
    } catch (error: any) {
      console.error('Error updating loyalty points:', error);
      // Fallback: update localStorage
      try {
        const saved = localStorage.getItem('loyalty_transactions') || '[]';
        const transactions = JSON.parse(saved);
        transactions.push({
          id: Date.now().toString(),
          ...transaction,
          date: new Date().toISOString()
        });
        localStorage.setItem('loyalty_transactions', JSON.stringify(transactions));
      } catch {}
      throw error;
    }
  }

  async getLoyaltyTransactions(userId?: string): Promise<any[]> {
    try {
      const userIdParam = userId || this.getUserIdFromToken();
      if (!userIdParam) {
        throw new Error('User ID is required');
      }
      
      const data = await this.getLoyaltyPoints(userIdParam);
      return data.transactions || [];
    } catch (error) {
      console.error('Error fetching loyalty transactions:', error);
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem('loyalty_transactions');
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    }
  }

  // Favorite Shops API
  async getFavoriteShops(userId?: string): Promise<string[]> {
    try {
      const userIdParam = userId || this.getUserIdFromToken();
      if (!userIdParam) {
        throw new Error('User ID is required for favorite shops operations');
      }
      
      console.log('🏪 Fetching favorite shops for user:', userIdParam);
      const data = await this.request<{ shop_ids: string[] }>(`/users/${userIdParam}/favorite-shops`);
      const shopIds = data.shop_ids || [];
      console.log('✅ Favorite shops loaded:', shopIds.length, 'shops');
      
      // Sync to localStorage as backup
      localStorage.setItem('favorite_shops', JSON.stringify(shopIds));
      
      return shopIds;
    } catch (error: any) {
      console.error('Error fetching favorite shops:', error);
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem('favorite_shops');
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    }
  }

  async addFavoriteShop(userId: string, shopId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🏪 Adding favorite shop:', shopId, 'for user:', userId);
      const response = await this.request<{ success: boolean; message?: string }>(`/users/${userId}/favorite-shops`, {
        method: 'POST',
        body: JSON.stringify({ shop_id: shopId }),
      });
      
      // Sync to localStorage as backup
      try {
        const currentFavorites = await this.getFavoriteShops(userId);
        localStorage.setItem('favorite_shops', JSON.stringify(currentFavorites));
      } catch {}
      
      console.log('✅ Shop added to favorites');
      return response;
    } catch (error: any) {
      console.error('Error adding favorite shop:', error);
      // Fallback: add to localStorage
      try {
        const saved = localStorage.getItem('favorite_shops') || '[]';
        const favorites = JSON.parse(saved);
        if (!favorites.includes(shopId)) {
          favorites.push(shopId);
          localStorage.setItem('favorite_shops', JSON.stringify(favorites));
        }
      } catch {}
      throw error;
    }
  }

  async removeFavoriteShop(userId: string, shopId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🏪 Removing favorite shop:', shopId, 'for user:', userId);
      const response = await this.request<{ success: boolean; message?: string }>(`/users/${userId}/favorite-shops/${shopId}`, {
        method: 'DELETE',
      });
      
      // Sync to localStorage as backup
      try {
        const currentFavorites = await this.getFavoriteShops(userId);
        localStorage.setItem('favorite_shops', JSON.stringify(currentFavorites));
      } catch {}
      
      console.log('✅ Shop removed from favorites');
      return response;
    } catch (error: any) {
      console.error('Error removing favorite shop:', error);
      // Fallback: remove from localStorage
      try {
        const saved = localStorage.getItem('favorite_shops') || '[]';
        const favorites = JSON.parse(saved);
        const updated = favorites.filter((id: string) => id !== shopId);
        localStorage.setItem('favorite_shops', JSON.stringify(updated));
      } catch {}
      throw error;
    }
  }

  // Notifications API
  async getNotifications(userId?: string): Promise<any[]> {
    try {
      const userIdParam = userId || this.getUserIdFromToken();
      if (!userIdParam) {
        throw new Error('User ID is required for notifications operations');
      }
      
      console.log('🔔 Fetching notifications for user:', userIdParam);
      const notifications = await this.request<any[]>(`/users/${userIdParam}/notifications`);
      console.log('✅ Notifications loaded:', notifications?.length || 0, 'notifications');
      
      // Sync to localStorage as backup
      if (notifications && notifications.length > 0) {
        localStorage.setItem('notifications', JSON.stringify(notifications));
      }
      
      return notifications || [];
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem('notifications');
        return saved ? JSON.parse(saved) : [];
      } catch {
        return [];
      }
    }
  }

  async markNotificationAsRead(userId: string, notificationId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🔔 Marking notification as read:', notificationId, 'for user:', userId);
      const response = await this.request<{ success: boolean; message?: string }>(`/users/${userId}/notifications/${notificationId}/read`, {
        method: 'PATCH',
      });
      
      // Sync to localStorage as backup
      try {
        const saved = localStorage.getItem('notifications') || '[]';
        const notifications = JSON.parse(saved);
        const updated = notifications.map((notif: any) =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        );
        localStorage.setItem('notifications', JSON.stringify(updated));
      } catch {}
      
      console.log('✅ Notification marked as read');
      return response;
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      // Fallback: update localStorage
      try {
        const saved = localStorage.getItem('notifications') || '[]';
        const notifications = JSON.parse(saved);
        const updated = notifications.map((notif: any) =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        );
        localStorage.setItem('notifications', JSON.stringify(updated));
      } catch {}
      throw error;
    }
  }

  async markAllNotificationsAsRead(userId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🔔 Marking all notifications as read for user:', userId);
      const response = await this.request<{ success: boolean; message?: string }>(`/users/${userId}/notifications/read-all`, {
        method: 'PATCH',
      });
      
      // Sync to localStorage as backup
      try {
        const saved = localStorage.getItem('notifications') || '[]';
        const notifications = JSON.parse(saved);
        const updated = notifications.map((notif: any) => ({ ...notif, read: true }));
        localStorage.setItem('notifications', JSON.stringify(updated));
      } catch {}
      
      console.log('✅ All notifications marked as read');
      return response;
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
      // Fallback: update localStorage
      try {
        const saved = localStorage.getItem('notifications') || '[]';
        const notifications = JSON.parse(saved);
        const updated = notifications.map((notif: any) => ({ ...notif, read: true }));
        localStorage.setItem('notifications', JSON.stringify(updated));
      } catch {}
      throw error;
    }
  }

  async deleteNotification(userId: string, notificationId: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🔔 Deleting notification:', notificationId, 'for user:', userId);
      const response = await this.request<{ success: boolean; message?: string }>(`/users/${userId}/notifications/${notificationId}`, {
        method: 'DELETE',
      });
      
      // Sync to localStorage as backup
      try {
        const saved = localStorage.getItem('notifications') || '[]';
        const notifications = JSON.parse(saved);
        const updated = notifications.filter((notif: any) => notif.id !== notificationId);
        localStorage.setItem('notifications', JSON.stringify(updated));
      } catch {}
      
      console.log('✅ Notification deleted');
      return response;
    } catch (error: any) {
      console.error('Error deleting notification:', error);
      // Fallback: remove from localStorage
      try {
        const saved = localStorage.getItem('notifications') || '[]';
        const notifications = JSON.parse(saved);
        const updated = notifications.filter((notif: any) => notif.id !== notificationId);
        localStorage.setItem('notifications', JSON.stringify(updated));
      } catch {}
      throw error;
    }
  }

  async createNotification(userId: string, notification: { message: string; type?: string; data?: any }): Promise<{ success: boolean; notification_id?: string }> {
    try {
      console.log('🔔 Creating notification for user:', userId, 'message:', notification.message);
      const response = await this.request<{ success: boolean; notification_id?: string }>(`/users/${userId}/notifications`, {
        method: 'POST',
        body: JSON.stringify({
          message: notification.message,
          type: notification.type || 'info',
          data: notification.data || {}
        }),
      });
      
      // Sync to localStorage as backup
      try {
        const saved = localStorage.getItem('notifications') || '[]';
        const notifications = JSON.parse(saved);
        const newNotification = {
          id: response.notification_id || `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          message: notification.message,
          type: notification.type || 'info',
          date: new Date().toISOString(),
          read: false,
          ...notification.data
        };
        notifications.unshift(newNotification); // Add to beginning
        localStorage.setItem('notifications', JSON.stringify(notifications));
        
        // Dispatch event to update notification count
        window.dispatchEvent(new CustomEvent('notificationCreated'));
      } catch {}
      
      console.log('✅ Notification created');
      return response;
    } catch (error: any) {
      console.error('Error creating notification:', error);
      // Fallback: save to localStorage
      try {
        const saved = localStorage.getItem('notifications') || '[]';
        const notifications = JSON.parse(saved);
        const newNotification = {
          id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          message: notification.message,
          type: notification.type || 'info',
          date: new Date().toISOString(),
          read: false,
          ...notification.data
        };
        notifications.unshift(newNotification);
        localStorage.setItem('notifications', JSON.stringify(notifications));
        window.dispatchEvent(new CustomEvent('notificationCreated'));
      } catch {}
      // Don't throw error - notification creation should not break the flow
      return { success: false };
    }
  }

  // Helper method to get user ID from token
  private getUserIdFromToken(): string | null {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;
      
      // Decode JWT token to get user ID
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id || payload.sub || payload.id || null;
    } catch {
      return null;
    }
  }

  // Service Bookings API
  // Base URL for service bookings - using dedicated service booking service
  private getServiceBookingsBase(): string {
    // Use dedicated service booking service URL if available, otherwise fallback
    const base = apiConfig.serviceBookingServiceUrl || apiConfig.orderServiceUrl || this.baseUrl;
    return base;
  }

  async getServiceProviders(categoryId?: string, filters?: {
    sortBy?: 'rating' | 'price' | 'distance';
    location?: string;
    minRating?: number;
    available?: boolean;
  }): Promise<any[]> {
    try {
      const serviceBase = this.getServiceBookingsBase();
      let endpoint = `${serviceBase}/services/providers`;
      const params = new URLSearchParams();
      
      if (categoryId) params.append('category', categoryId);
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.location) params.append('location', filters.location);
      if (filters?.minRating) params.append('minRating', filters.minRating.toString());
      if (filters?.available !== undefined) params.append('available', filters.available.toString());
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      console.log('🔧 Service API: Fetching providers from:', endpoint);
      console.log('🔧 Service API: Category filter:', categoryId || 'none (all providers)');
      
      // Use fetch directly with the full URL instead of this.request()
      const headers = this.getHeaders();
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: headers,
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Log if response needs client-side filtering
      if (categoryId && Array.isArray(data)) {
        const matchingProviders = data.filter((p: any) => 
          p.category === categoryId || 
          p.category?.toLowerCase() === categoryId?.toLowerCase()
        );
        if (matchingProviders.length !== data.length) {
          console.warn(`⚠️ API returned ${data.length} providers, but only ${matchingProviders.length} match category "${categoryId}"`);
          console.warn('   Client-side filtering will be applied');
        }
      }
      
      return data;
    } catch (error: any) {
      console.error('🔧 Service API: Error fetching providers:', error);
      // Fallback to mock data for development
      if (apiConfig.debug) {
        console.warn('⚠️ Service API: Using mock data fallback');
        return this.getMockProviders(categoryId);
      }
      throw error;
    }
  }

  async getServiceProvider(providerId: string): Promise<any> {
    try {
      const serviceBase = this.getServiceBookingsBase();
      const endpoint = `${serviceBase}/services/providers/${providerId}`;
      console.log('🔧 Service API: Fetching provider from:', endpoint);
      
      // Use fetch directly with the full URL instead of this.request()
      const headers = this.getHeaders();
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: headers,
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('🔧 Service API: Error fetching provider:', error);
      // Fallback to mock data for development
      if (apiConfig.debug) {
        console.warn('⚠️ Service API: Using mock data fallback');
        return this.getMockProvider(providerId);
      }
      throw error;
    }
  }

  async createServiceBooking(bookingData: {
    providerId: string;
    customerId: string;
    date: 'today' | 'tomorrow' | string;
    time: string;
    address: string;
    description?: string;
  }): Promise<any> {
    try {
      // Validate required fields
      if (!bookingData.providerId) {
        throw new Error('Missing required field: providerId');
      }
      if (!bookingData.customerId) {
        throw new Error('Missing required field: customerId');
      }
      if (!bookingData.date) {
        throw new Error('Missing required field: date');
      }
      if (!bookingData.time) {
        throw new Error('Missing required field: time');
      }
      if (!bookingData.address) {
        throw new Error('Missing required field: address');
      }

      const serviceBase = this.getServiceBookingsBase();
      const endpoint = `${serviceBase}/services/bookings`;
      console.log('🔧 Service API: Creating booking:', bookingData);
      console.log('🔧 Service API: Using endpoint:', endpoint);
      console.log('🔧 Service API: Service base URL:', serviceBase);
      
      try {
        // Use fetch directly with the full URL instead of this.request()
        const headers = this.getHeaders();
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(bookingData),
          mode: 'cors',
          credentials: 'omit',
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { detail: errorText, message: errorText };
          }
          const error = new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
          (error as any).status = response.status;
          (error as any).response = errorData;
          throw error;
        }
        
        const data = await response.json();
        
        console.log('🔧 Service API: Booking created:', data);
        return data;
      } catch (apiError: any) {
        // If endpoint doesn't exist (404) or service unavailable, use mock response for development
        const is404 = apiError?.status === 404 || 
                      apiError?.message?.includes('404') || 
                      apiError?.message?.includes('Not Found');
        const isNetworkError = apiError?.message?.includes('Network') || 
                              apiError?.isNetworkError;
        
        if (is404 || isNetworkError || apiConfig.debug) {
          console.warn('⚠️ Service API: Endpoint not available, using mock booking response for development');
          console.warn('⚠️ Error details:', { 
            status: apiError?.status, 
            message: apiError?.message,
            is404,
            isNetworkError 
          });
          
          // Create mock booking response
          const mockBooking = {
            id: `booking-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            bookingId: `BK${Date.now()}`,
            providerId: bookingData.providerId,
            customerId: bookingData.customerId,
            date: bookingData.date,
            time: bookingData.time,
            address: bookingData.address,
            description: bookingData.description,
            status: 'pending',
            createdAt: new Date().toISOString(),
            _isMock: true // Flag to indicate this is a mock response
          };
          
          // Store in localStorage for development (so My Bookings can show it)
          try {
            const existingBookings = JSON.parse(localStorage.getItem('mockServiceBookings') || '[]');
            existingBookings.push(mockBooking);
            localStorage.setItem('mockServiceBookings', JSON.stringify(existingBookings));
            console.log('✅ Mock booking saved to localStorage');
          } catch (e) {
            console.warn('Could not save mock booking to localStorage:', e);
          }
          
          return mockBooking;
        }
        
        // Re-throw if it's a different error
        throw apiError;
      }
    } catch (error: any) {
      console.error('🔧 Service API: Error creating booking:', error);
      throw error;
    }
  }

  async getServiceBooking(bookingId: string): Promise<any> {
    try {
      const serviceBase = this.getServiceBookingsBase();
      const endpoint = `${serviceBase}/services/bookings/${bookingId}`;
      console.log('🔧 Service API: Fetching booking from:', endpoint);
      
      // Use fetch directly with the full URL instead of this.request()
      const headers = this.getHeaders();
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: headers,
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('🔧 Service API: Error fetching booking:', error);
      throw error;
    }
  }

  async getUserServiceBookings(customerId?: string): Promise<any[]> {
    try {
      const userId = customerId || this.getUserIdFromToken();
      if (!userId) {
        throw new Error('User ID is required');
      }
      
      const serviceBase = this.getServiceBookingsBase();
      const endpoint = `${serviceBase}/services/bookings?customerId=${userId}`;
      console.log('🔧 Service API: Fetching user bookings from:', endpoint);
      
      try {
        // Use fetch directly with the full URL instead of this.request()
        const headers = this.getHeaders();
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: headers,
          mode: 'cors',
          credentials: 'omit',
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data;
      } catch (apiError: any) {
        // If endpoint doesn't exist, return mock bookings from localStorage
        if (apiError?.message?.includes('404') || 
            apiError?.message?.includes('Not Found') ||
            apiConfig.debug) {
          console.warn('⚠️ Service API: Endpoint not available, using mock bookings from localStorage');
          try {
            const mockBookings = JSON.parse(localStorage.getItem('mockServiceBookings') || '[]');
            // Filter by customerId if provided
            const filtered = customerId 
              ? mockBookings.filter((b: any) => b.customerId === customerId || b.customerId === userId)
              : mockBookings;
            return filtered;
          } catch (e) {
            console.warn('Could not load mock bookings from localStorage:', e);
            return [];
          }
        }
        throw apiError;
      }
    } catch (error: any) {
      console.error('🔧 Service API: Error fetching user bookings:', error);
      throw error;
    }
  }

  async updateServiceBookingStatus(bookingId: string, status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled'): Promise<any> {
    try {
      const serviceBase = this.getServiceBookingsBase();
      const endpoint = `${serviceBase}/services/bookings/${bookingId}/status`;
      console.log('🔧 Service API: Updating booking status:', { bookingId, status });
      
      // Use fetch directly with the full URL instead of this.request()
      const headers = this.getHeaders();
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify({ status }),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('🔧 Service API: Error updating booking status:', error);
      throw error;
    }
  }

  async cancelServiceBooking(bookingId: string, reason?: string): Promise<any> {
    try {
      const serviceBase = this.getServiceBookingsBase();
      const endpoint = `${serviceBase}/services/bookings/${bookingId}/cancel`;
      console.log('🔧 Service API: Cancelling booking:', bookingId);
      
      // Use fetch directly with the full URL instead of this.request()
      const headers = this.getHeaders();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ reason }),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('🔧 Service API: Error cancelling booking:', error);
      throw error;
    }
  }

  // Mock data fallback methods for development
  private getMockProviders(categoryId?: string): any[] {
    // Return different providers for different categories
    const baseProviders: any[] = [
      {
        id: '1',
        name: 'Rajesh Kumar',
        category: 'plumbing',
        rating: 4.8,
        reviews: 124,
        price: '₹300-500',
        experience: '8 years',
        location: 'Sector 15, Noida',
        distance: '2.5 km',
        available: true,
        verified: true,
        skills: ['Pipe repair', 'Tap installation', 'Water tank'],
        languages: ['Hindi', 'English'],
        responseTime: '30 min'
      },
      {
        id: '2',
        name: 'Amit Singh',
        category: 'plumbing',
        rating: 4.6,
        reviews: 89,
        price: '₹250-400',
        experience: '5 years',
        location: 'Sector 18, Noida',
        distance: '3.2 km',
        available: true,
        verified: true,
        skills: ['Leak fixing', 'Bathroom repair'],
        languages: ['Hindi'],
        responseTime: '45 min'
      },
      {
        id: '3',
        name: 'Vikram Mehta',
        category: 'plumbing',
        rating: 4.9,
        reviews: 156,
        price: '₹400-600',
        experience: '12 years',
        location: 'Sector 12, Noida',
        distance: '1.8 km',
        available: false,
        verified: true,
        skills: ['Complete plumbing', 'RO installation'],
        languages: ['Hindi', 'English', 'Punjabi'],
        responseTime: '20 min'
      },
      {
        id: '4',
        name: 'Suresh Carpenter',
        category: 'carpenter',
        rating: 4.7,
        reviews: 98,
        price: '₹500-800',
        experience: '10 years',
        location: 'Sector 20, Noida',
        distance: '4.1 km',
        available: true,
        verified: true,
        skills: ['Furniture making', 'Door installation', 'Window repair'],
        languages: ['Hindi', 'English'],
        responseTime: '45 min'
      },
      {
        id: '5',
        name: 'Ramesh Electrician',
        category: 'electrical',
        rating: 4.5,
        reviews: 112,
        price: '₹350-600',
        experience: '7 years',
        location: 'Sector 16, Noida',
        distance: '2.8 km',
        available: true,
        verified: true,
        skills: ['Wiring', 'Switch installation', 'Electrical repair'],
        languages: ['Hindi'],
        responseTime: '30 min'
      }
    ];
    
    // If categoryId is provided, filter to only that category
    if (categoryId) {
      return baseProviders.filter(p => p.category === categoryId);
    }
    
    // Otherwise return all
    return baseProviders;
  }

  private getMockProvider(providerId: string): any {
    return {
      id: providerId,
      name: 'Rajesh Kumar',
      category: 'plumbing',
      rating: 4.8,
      reviews: 124,
      price: '₹300-500',
      experience: '8 years',
      location: 'Sector 15, Noida',
      distance: '2.5 km',
      available: true,
      verified: true,
      skills: ['Pipe repair', 'Tap installation', 'Water tank', 'RO installation', 'Bathroom repair'],
      languages: ['Hindi', 'English'],
      responseTime: '30 min',
      description: 'Experienced plumber with 8+ years of expertise in residential and commercial plumbing. Specialized in pipe repairs, installations, and water system maintenance.',
      photos: [],
      availability: {
        today: true,
        tomorrow: true,
        slots: ['10:00 AM', '2:00 PM', '4:00 PM']
      },
      phone: '+919876543210',
      whatsapp: '+919876543210'
    };
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export the class for testing or custom instances
export default ApiService;



